import { DateAsString, ForsikringDekningDTO, SporsmalType } from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import { BesvartSporsmal, CurrentSporsmal } from '../reducer/current-steg';
import { ForsikringAlternativ, Forsikringsporsmal } from '../reducer/sporsmalsflyt-types';
import { filterForsikringerBySkadeDatoAndPrioritization } from './forsikring-utils';

export enum ForsikringDataTypes {
    Forsikringsliste = 'Forsikringsliste',
    ForsikringIngenAlternativer = 'ForsikringIngenAlternativer',
}

export interface CurrentForsikringListeData {
    type: ForsikringDataTypes.Forsikringsliste;
    alternativer: ForsikringAlternativ[];
    selectedAlternativ?: ForsikringAlternativ;
}

export interface CurrentForsikringIngenAlternativerData {
    type: ForsikringDataTypes.ForsikringIngenAlternativer;
    forsikringsnummer: string;
}

export type CurrentForsikringData = CurrentForsikringListeData | CurrentForsikringIngenAlternativerData;

export interface CurrentForsikringsporsmal {
    type: SporsmalType.Forsikring;
    sporsmal: Forsikringsporsmal;
    data: CurrentForsikringData;
}

export interface BesvartForsikringsListeData {
    type: ForsikringDataTypes.Forsikringsliste;
    alternativer: ForsikringAlternativ[];
    selectedAlternativ: ForsikringAlternativ;
}

export type BesvartForsikringIngenAlternativerData = CurrentForsikringIngenAlternativerData;

export type BesvartForsikringData = BesvartForsikringsListeData | BesvartForsikringIngenAlternativerData;

export interface BesvartForsikringsporsmal {
    type: SporsmalType.Forsikring;
    sporsmal: Forsikringsporsmal;
    data: BesvartForsikringData;
}

export const isForsikringSporsmal = (besvartSporsmal: CurrentSporsmal): besvartSporsmal is CurrentForsikringsporsmal =>
    besvartSporsmal.type === SporsmalType.Forsikring;

export const isForsikringListe = (data: CurrentForsikringData): data is CurrentForsikringListeData =>
    data.type === ForsikringDataTypes.Forsikringsliste;

export const isForsikringIngenAlternativerDataType = (
    data: CurrentForsikringData,
): data is CurrentForsikringIngenAlternativerData => data.type === ForsikringDataTypes.ForsikringIngenAlternativer;

export const isBesvartForsikringSporsmal = (
    currentSporsmal: CurrentForsikringsporsmal,
): currentSporsmal is BesvartForsikringsporsmal =>
    (isForsikringListe(currentSporsmal.data) && !!currentSporsmal.data.selectedAlternativ) ||
    isForsikringIngenAlternativerDataType(currentSporsmal.data);

const updateDekningListeSporsmalSteg = (
    opprinneligDekningssvar: CurrentForsikringListeData,
    selectedAlternativ?: ForsikringAlternativ,
): CurrentForsikringListeData => {
    return { ...opprinneligDekningssvar, selectedAlternativ };
};

export const updateCurrentForsikringSporsmal = (
    currentSporsmal: CurrentSporsmal,
    selectedAlternativ?: ForsikringAlternativ,
): CurrentSporsmal => {
    if (isForsikringSporsmal(currentSporsmal) && isForsikringListe(currentSporsmal.data)) {
        return {
            ...currentSporsmal,
            data: updateDekningListeSporsmalSteg(currentSporsmal.data, selectedAlternativ),
        };
    }
    return currentSporsmal;
};

export const forsikringSporsmalFinnerIkkeRiktigAlternativ = (tekst: string): ForsikringAlternativ => ({
    id: ForsikringDekningDTO.FinnerIkkeRiktig,
    tekst,
    forsikringsnummer: '',
    forsikringversjon: null,
    undertekst: '',
});

export const toCurrentForsikringSporsmal = (
    sporsmal: Forsikringsporsmal,
    skadeDato: DateAsString,
    kundeinfo: Kundeinfo,
): CurrentForsikringsporsmal => {
    const forsikringer = kundeinfo?.forsikringer || [];
    const forsikringsalternativer: ForsikringAlternativ[] = filterForsikringerBySkadeDatoAndPrioritization(
        forsikringer,
        new Date(skadeDato),
        sporsmal.forsikringsprodukterPriority,
    ).map((forsikring) => ({
        id: forsikring.dekning,
        tekst: forsikring.visningstekst,
        forsikringsnummer: forsikring.forsikringsnummer,
        forsikringversjon: forsikring.versjon,
        undertekst: forsikring.info,
        forsikringsperiode: forsikring.periode,
        forsikringsprodukt: forsikring.produkt,
    }));

    if (forsikringsalternativer.length === 0) {
        return {
            type: SporsmalType.Forsikring,
            sporsmal,
            data: {
                type: ForsikringDataTypes.ForsikringIngenAlternativer,
                forsikringsnummer: '',
            },
        };
    }

    return {
        type: SporsmalType.Forsikring,
        sporsmal,
        data: {
            type: ForsikringDataTypes.Forsikringsliste,
            alternativer: [
                ...forsikringsalternativer,
                forsikringSporsmalFinnerIkkeRiktigAlternativ(sporsmal.finnerIkkeRiktigForsikringAlternativTekst),
            ],
        },
    };
};

export const isBesvartForsikringsporsmal = (
    besvartSporsmal: BesvartSporsmal,
): besvartSporsmal is BesvartForsikringsporsmal => besvartSporsmal && besvartSporsmal.type === SporsmalType.Forsikring;

export const findChosenForsikringFromSporsmal = (besvarteSporsmal: BesvartSporsmal[]): ForsikringAlternativ | null => {
    const forsikringsporsmal: BesvartForsikringsporsmal | null = besvarteSporsmal.find(isBesvartForsikringsporsmal);
    if (forsikringsporsmal && isForsikringListe(forsikringsporsmal?.data)) {
        return forsikringsporsmal.data.selectedAlternativ;
    }

    return null;
};
